import { useEffect, type RefObject } from "react";
import { canUseCinematicMotion, gsap, ScrollTrigger } from "../lib/motion";

interface ScrollRevealOptions {
  /** Selector within the container for staggered children. Default: [data-reveal] */
  childSelector?: string;
  y?: number;
  duration?: number;
  stagger?: number;
  start?: string;
}

/**
 * Fade/slide-in children when the container enters the viewport.
 * No-ops when prefers-reduced-motion is set.
 */
export function useScrollReveal(
  containerRef: RefObject<HTMLElement | null>,
  options: ScrollRevealOptions = {}
): void {
  const {
    childSelector = "[data-reveal]",
    y = 48,
    duration = 0.85,
    stagger = 0.12,
    start = "top 85%",
  } = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !canUseCinematicMotion()) return;

    const targets = container.querySelectorAll(childSelector);
    if (!targets.length) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        targets,
        { y, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration,
          stagger,
          ease: "power2.out",
          scrollTrigger: {
            trigger: container,
            start,
            toggleActions: "play none none none",
          },
        }
      );
    }, container);

    return () => {
      ctx.revert();
    };
  }, [containerRef, childSelector, y, duration, stagger, start]);
}

/**
 * Animate skill progress fills when the skills section enters view.
 * Pass `enabled` when the container mounts conditionally (e.g. collapsed Background).
 */
export function useSkillBarReveal(
  containerRef: RefObject<HTMLElement | null>,
  enabled = true
): void {
  useEffect(() => {
    if (!enabled) return;

    let ctx: gsap.Context | null = null;
    let rafId = 0;
    let cancelled = false;

    // Wait for paint so conditionally-mounted fills exist in the DOM
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        if (cancelled) return;

        const container = containerRef.current;
        if (!container) return;

        const fills =
          container.querySelectorAll<HTMLElement>("[data-skill-fill]");
        if (!fills.length) return;

        fills.forEach((el) => {
          el.style.width = "0%";
        });

        if (!canUseCinematicMotion()) {
          fills.forEach((el) => {
            el.style.width = el.dataset.width || "0%";
          });
          return;
        }

        ctx = gsap.context(() => {
          fills.forEach((el) => {
            const width = el.dataset.width || "0%";
            gsap.to(el, {
              width,
              duration: 1.2,
              ease: "power2.out",
              scrollTrigger: {
                trigger: el,
                start: "top 90%",
                toggleActions: "play none none none",
              },
            });
          });
        }, container);

        // Section is often already in view when expanded — refresh so triggers fire
        ScrollTrigger.refresh();
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      ctx?.revert();
    };
  }, [containerRef, enabled]);
}
